import { useEffect, useMemo, useState, type ChangeEvent, type FormEvent, type JSX } from 'react';
import { Pencil2Icon } from '@radix-ui/react-icons';
import clsx from 'clsx';
import { toast } from 'sonner';
import type { ZodIssue } from 'zod';

import { Form, FormActions, FormField, FormMessage } from '@/components/form';
import { PillButton } from '@/components/entity/PillButton';
import { useDashboardUser } from '@/routes/dashboard/DashboardRouter';
import {
  useDashboardPropertyDetail,
  usePropertyBasicsMutation,
} from '@/hooks/useDashboardProperties';
import type {
  AssetType,
  FloorPlanMode,
  ProvinceCode,
  PropertyFormValues,
  PropertySummary,
  StreetDirection,
  StreetType,
} from '@/lib/types/property';
import { formatPropertyAddress } from '@/lib/utils/propertyFormatting';
import {
  PropertyActionIconButton,
  propertyActionIconClassName,
} from '@/components/dashboard/properties/PropertyActionIconButton';
import {
  PROPERTY_HERO_MEDIA_ALLOWED_TYPES,
  PROPERTY_MEDIA_ALLOWED_TYPES,
  PROPERTY_MEDIA_MAX_FILES,
  PROPERTY_MEDIA_MAX_BYTES,
} from '@/lib/constants/propertyMedia';
import {
  ASSET_TYPE_LABELS,
  ASSET_TYPE_OPTIONS,
  SUBMARKET_OPTIONS,
} from '@/lib/constants/propertyOptions';
import {
  CANADIAN_POSTAL_CODE_PATTERN,
  PROPERTY_CODE_PATTERN,
} from '@/lib/constants/validationPatterns';
import {
  isAllowedPropertyHeroMediaType,
  isAllowedPropertyMediaType,
  isPropertyMediaVideoUrl,
  parsePropertyMediaUrl,
  removePropertyMediaFiles,
  isWithinPropertyMediaSizeLimit,
  uploadPropertyMediaFile,
} from '@/lib/storage/propertyMedia';
import { Drawer } from '@/components/layout/Drawer';
import { propertyFormSchema } from '@/lib/validation/property';

type ButtonVariant = 'primary' | 'secondary';
type ButtonSize = 'sm' | 'md';

interface PropertyBasicsDrawerProps {
  property: PropertySummary;
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  className?: string;
  mode?: 'text' | 'icon';
}

type FormState = {
  propertyCode: string;
  submarket: string;
  assetType: AssetType;
  totalRentableSf: string;
  marketingSummary: string;
  floorPlanMode: FloorPlanMode;
  floorPlanImageUrl: string;
  streetNumber: string;
  streetName: string;
  streetType: StreetType;
  streetDirection: StreetDirection | '';
  suiteNumber: string;
  city: string;
  province: ProvinceCode;
  postalCode: string;
  heroMediaUrls: string[];
  uploading: boolean;
  uploadError: string | null;
};

type FormErrorKey = Exclude<keyof FormState, 'uploading' | 'uploadError'>;
type FormErrors = Partial<Record<FormErrorKey, string>>;

const STREET_TYPE_OPTIONS: StreetType[] = [
  'Ave',
  'Blvd',
  'Cir',
  'Cres',
  'Dr',
  'Gate',
  'Lane',
  'Pkwy',
  'Rd',
  'St',
  'Terr',
  'Way',
];
const STREET_DIRECTION_OPTIONS: StreetDirection[] = ['N', 'S', 'E', 'W', 'NE', 'NW', 'SE', 'SW'];
const PROVINCE_OPTIONS: ProvinceCode[] = [
  'AB',
  'BC',
  'MB',
  'NB',
  'NL',
  'NS',
  'NT',
  'NU',
  'ON',
  'PE',
  'QC',
  'SK',
  'YT',
];
const FLOOR_PLAN_MODE_OPTIONS: FloorPlanMode[] = ['hotspot', 'blank_canvas'];
const FORM_ERROR_KEYS: FormErrorKey[] = [
  'propertyCode',
  'submarket',
  'assetType',
  'totalRentableSf',
  'marketingSummary',
  'floorPlanMode',
  'floorPlanImageUrl',
  'streetNumber',
  'streetName',
  'streetType',
  'streetDirection',
  'suiteNumber',
  'city',
  'province',
  'postalCode',
  'heroMediaUrls',
];
const FORM_ERROR_KEY_SET = new Set(FORM_ERROR_KEYS);

function isFormErrorKey(value: unknown): value is FormErrorKey {
  return typeof value === 'string' && FORM_ERROR_KEY_SET.has(value as FormErrorKey);
}

export function PropertyBasicsDrawer({
  property,
  variant = 'secondary',
  size = 'md',
  fullWidth = false,
  className,
  mode = 'text',
}: PropertyBasicsDrawerProps): JSX.Element {
  const user = useDashboardUser();
  const detailQuery = useDashboardPropertyDetail({ user, propertyId: property.id });
  const mutation = usePropertyBasicsMutation({ user });

  const sourceProperty: PropertyFormValues = useMemo(() => {
    if (detailQuery.data?.property) {
      return detailQuery.data.property;
    }
    return mapSummaryToFormValues(property);
  }, [detailQuery.data?.property, property]);

  const [open, setOpen] = useState(false);
  const [formState, setFormState] = useState<FormState>(() => buildFormState(sourceProperty));
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const clearFieldError = (field: FormErrorKey) => {
    setErrors((current) => {
      if (typeof current[field] !== 'string') {
        return current;
      }
      const { [field]: _removed, ...rest } = current;
      return rest;
    });
  };
  const errorFor = (field: FormErrorKey): string | null => {
    const message = errors[field];
    return typeof message === 'string' ? message : null;
  };
  const renderFieldError = (field: FormErrorKey, className = 'mt-1 text-xs text-red-600') => {
    const message = errorFor(field);
    if (message === null) {
      return null;
    }
    return <p className={className}>{message}</p>;
  };
  const updateField = <K extends keyof FormState>(
    key: K,
    transform?: (value: string) => FormState[K],
  ) => {
    return (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const nextValue = transform
        ? transform(event.target.value)
        : (event.target.value as FormState[K]);
      setFormState((current) => ({
        ...current,
        [key]: nextValue,
      }));
      if (isFormErrorKey(key)) {
        clearFieldError(key);
      }
    };
  };

  useEffect(() => {
    setFormState(buildFormState(sourceProperty));
    setErrors({});
    setError(null);
  }, [sourceProperty]);

  const isLoading = detailQuery.isLoading;
  const isSaving = mutation.isPending || formState.uploading;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    if (!event.currentTarget.reportValidity()) {
      return;
    }
    event.preventDefault();
    setError(null);
    setErrors({});
    const correlationId = createCorrelationId();

    const trimmedHeroUrls = formState.heroMediaUrls
      .map((url) => url.trim())
      .filter((url) => url.length > 0);
    const trimmedFloorPlanUrl = formState.floorPlanImageUrl.trim();
    const payload: PropertyFormValues = {
      ...sourceProperty,
      propertyCode: formState.propertyCode.trim(),
      submarket: formState.submarket.trim(),
      assetType: formState.assetType,
      totalRentableSf: Number(formState.totalRentableSf) || 0,
      marketingSummary: formState.marketingSummary.trim(),
      heroMediaUrls: trimmedHeroUrls,
      floorPlanMode: formState.floorPlanMode,
      floorPlanImageUrl: trimmedFloorPlanUrl.length > 0 ? trimmedFloorPlanUrl : null,
      floorPlanConfig: sourceProperty.floorPlanConfig,
      address: {
        streetNumber: formState.streetNumber.trim(),
        streetName: formState.streetName.trim(),
        streetType: formState.streetType,
        streetDirection: formState.streetDirection === '' ? null : formState.streetDirection,
        suiteNumber: formState.suiteNumber.trim() || null,
        city: formState.city.trim(),
        province: formState.province,
        postalCode: formState.postalCode.trim(),
      },
      createdAt: sourceProperty.createdAt,
      updatedAt: sourceProperty.updatedAt,
      createdBy: sourceProperty.createdBy,
      updatedBy: sourceProperty.updatedBy,
    };

    const validation = propertyFormSchema.safeParse(payload);
    if (!validation.success) {
      const issues = validation.error.issues.map((issue) => ({
        path: issue.path,
        message: issue.message,
      }));
      const mapped = mapIssues(validation.error.issues);
      setErrors(mapped);
      const validationMessage = issues.at(0)?.message ?? 'Fix the highlighted fields.';
      setError(validationMessage);
      console.warn(
        JSON.stringify({
          ts: new Date().toISOString(),
          level: 'warn',
          event: 'property_form_validation_failed',
          correlationId,
          propertyId: property.id,
          issues,
        }),
      );
      return;
    }

    console.info(
      JSON.stringify({
        ts: new Date().toISOString(),
        level: 'info',
        event: 'property_form_submit_start',
        correlationId,
        propertyId: property.id,
      }),
    );

    mutation.mutate(
      { propertyId: property.id, values: validation.data },
      {
        onSuccess: () => {
          toast.success('Property updated.');
          console.info(
            JSON.stringify({
              ts: new Date().toISOString(),
              level: 'info',
              event: 'property_form_submit_success',
              correlationId,
              propertyId: property.id,
            }),
          );
          setErrors({});
          setError(null);
          setFormState(buildFormState(validation.data));
          setOpen(false);
        },
        onError: (reason) => {
          const message = reason instanceof Error ? reason.message : 'Unable to update property.';
          setError(message);
          toast.error(message);
          console.error(
            JSON.stringify({
              ts: new Date().toISOString(),
              level: 'error',
              event: 'property_form_submit_error',
              correlationId,
              propertyId: property.id,
              message,
            }),
          );
        },
      },
    );
  };

  const triggerClassName = clsx(fullWidth && 'w-full', className);
  const mediaList = useMemo(
    () => formState.heroMediaUrls.filter((url) => url.trim().length > 0),
    [formState.heroMediaUrls],
  );
  const maxImageSizeGb = Math.round(PROPERTY_MEDIA_MAX_BYTES / (1024 * 1024 * 1024));
  const floorPlanList = useMemo(
    () => (formState.floorPlanImageUrl.length > 0 ? [formState.floorPlanImageUrl] : []),
    [formState.floorPlanImageUrl],
  );
  const isHeroGalleryFull = mediaList.length >= PROPERTY_MEDIA_MAX_FILES;

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files ?? []);
    if (selectedFiles.length === 0) {
      return;
    }

    setFormState((current) => ({ ...current, uploadError: null }));

    const remainingSlots = PROPERTY_MEDIA_MAX_FILES - mediaList.length;
    if (remainingSlots <= 0) {
      setFormState((current) => ({
        ...current,
        uploadError: `You can upload up to ${PROPERTY_MEDIA_MAX_FILES.toString()} media files.`,
      }));
      event.target.value = '';
      return;
    }

    const files = selectedFiles.slice(0, remainingSlots);
    if (selectedFiles.length > remainingSlots) {
      toast.info(
        `Uploading ${remainingSlots.toString()} of ${selectedFiles.length.toString()} selected files.`,
      );
    }

    const invalidType = files.find((file) => !isAllowedPropertyHeroMediaType(file));
    if (invalidType) {
      setFormState((current) => ({
        ...current,
        uploadError: `Unsupported file type: ${invalidType.type || invalidType.name}.`,
      }));
      event.target.value = '';
      return;
    }

    const tooLarge = files.find((file) => !isWithinPropertyMediaSizeLimit(file));
    if (tooLarge) {
      setFormState((current) => ({
        ...current,
        uploadError: `Each media file must be under ${maxImageSizeGb.toString()}GB.`,
      }));
      event.target.value = '';
      return;
    }

    setFormState((current) => ({ ...current, uploading: true, uploadError: null }));
    let uploadedCount = 0;
    try {
      for (const file of files) {
        const result = await uploadPropertyMediaFile({ propertyId: property.id, file });
        if (typeof result.url === 'string' && result.url.length > 0) {
          uploadedCount += 1;
          setFormState((current) => ({
            ...current,
            heroMediaUrls: [...current.heroMediaUrls, result.url],
          }));
          clearFieldError('heroMediaUrls');
        }
      }

      if (uploadedCount === 1) {
        toast.success('Uploaded media.');
      } else if (uploadedCount > 1) {
        toast.success(`Uploaded ${uploadedCount.toString()} media files.`);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Upload failed. Please try again.';
      setFormState((current) => ({ ...current, uploadError: message }));
      toast.error(message);
    } finally {
      setFormState((current) => ({ ...current, uploading: false }));
      event.target.value = '';
    }
  };

  const handleFloorPlanFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []).slice(0, 1);
    if (files.length === 0) {
      return;
    }

    setFormState((current) => ({ ...current, uploadError: null, uploading: true }));

    const invalidType = files.find((file) => !isAllowedPropertyMediaType(file));
    if (invalidType) {
      setFormState((current) => ({
        ...current,
        uploading: false,
        uploadError: `Unsupported file type: ${invalidType.type || invalidType.name}.`,
      }));
      event.target.value = '';
      return;
    }

    const tooLarge = files.find((file) => !isWithinPropertyMediaSizeLimit(file));
    if (tooLarge) {
      setFormState((current) => ({
        ...current,
        uploading: false,
        uploadError: `Each image must be under ${maxImageSizeGb.toString()}GB.`,
      }));
      event.target.value = '';
      return;
    }

    try {
      const [file] = files;

      const currentUrl = floorPlanList.at(0);
      if (typeof currentUrl === 'string' && currentUrl.length > 0) {
        const parsedCurrent = parsePropertyMediaUrl(currentUrl);
        if (parsedCurrent) {
          await removePropertyMediaFiles([`${parsedCurrent.bucket}/${parsedCurrent.objectPath}`]);
        }
      }

      const result = await uploadPropertyMediaFile({ propertyId: property.id, file });
      if (typeof result.url === 'string' && result.url.length > 0) {
        setFormState((current) => ({
          ...current,
          floorPlanImageUrl: result.url,
        }));
        clearFieldError('floorPlanImageUrl');
        toast.success('Uploaded floor plan image.');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Upload failed. Please try again.';
      setFormState((current) => ({ ...current, uploadError: message }));
      toast.error(message);
    } finally {
      setFormState((current) => ({ ...current, uploading: false }));
      event.target.value = '';
    }
  };

  const handleRemoveFloorPlanImage = async () => {
    setFormState((current) => ({ ...current, uploading: true, uploadError: null }));
    let shouldClear = true;
    try {
      const currentUrl = floorPlanList.at(0);
      if (typeof currentUrl === 'string' && currentUrl.length > 0) {
        const parsed = parsePropertyMediaUrl(currentUrl);
        if (parsed) {
          await removePropertyMediaFiles([`${parsed.bucket}/${parsed.objectPath}`]);
        }
      }
    } catch (error) {
      shouldClear = false;
      const message = error instanceof Error ? error.message : 'Unable to remove floor plan image.';
      setFormState((current) => ({ ...current, uploadError: message }));
      toast.error(message);
    } finally {
      setFormState((current) => ({
        ...current,
        floorPlanImageUrl: shouldClear ? '' : current.floorPlanImageUrl,
        uploading: false,
      }));
      if (shouldClear) {
        clearFieldError('floorPlanImageUrl');
      }
    }
  };

  const handleRemoveMedia = async (url: string) => {
    setFormState((current) => ({ ...current, uploading: true, uploadError: null }));
    let shouldRemove = true;
    try {
      const parsed = parsePropertyMediaUrl(url);
      if (parsed) {
        await removePropertyMediaFiles([`${parsed.bucket}/${parsed.objectPath}`]);
      }
    } catch (error) {
      shouldRemove = false;
      const message =
        error instanceof Error ? error.message : 'Unable to remove media from storage.';
      setFormState((current) => ({ ...current, uploadError: message }));
      toast.error(message);
    } finally {
      setFormState((current) => ({
        ...current,
        heroMediaUrls: shouldRemove
          ? current.heroMediaUrls.filter((existing) => existing !== url)
          : current.heroMediaUrls,
        uploading: false,
      }));
      if (shouldRemove) {
        clearFieldError('heroMediaUrls');
      }
    }
  };

  const formId = 'property-basics-form';

  return (
    <Drawer
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setError(null);
          setErrors({});
          setFormState(buildFormState(sourceProperty));
        }
        setOpen(next);
      }}
      title="Edit property"
      description={formatPropertyAddress(property.address)}
      trigger={
        mode === 'icon' ? (
          <PropertyActionIconButton label="Edit property">
            <button
              type="button"
              aria-label="Edit property"
              className={clsx(propertyActionIconClassName, triggerClassName)}
              onClick={() => {
                setError(null);
                setErrors({});
                setOpen(true);
              }}
            >
              <Pencil2Icon className="h-4 w-4" />
              <span className="sr-only">Edit property</span>
            </button>
          </PropertyActionIconButton>
        ) : (
          <PillButton
            type="button"
            variant={variant}
            size={size}
            className={triggerClassName}
            onClick={() => {
              setError(null);
              setErrors({});
              setOpen(true);
            }}
          >
            Edit property
          </PillButton>
        )
      }
      footerContent={
        <FormActions justify="between">
          <p className="text-xs text-text-primary/60">
            Updates apply immediately across the dashboard and public listings.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              className="rounded-full px-4 py-2 text-sm font-semibold text-text-primary/70 transition hover:bg-surface-subtle"
              onClick={() => {
                setError(null);
                setErrors({});
                setFormState(buildFormState(sourceProperty));
                setOpen(false);
              }}
            >
              Cancel
            </button>
            <PillButton form={formId} type="submit" disabled={isSaving}>
              {isSaving ? 'Saving…' : 'Save changes'}
            </PillButton>
          </div>
        </FormActions>
      }
    >
      <div className="flex flex-col gap-4">
        {error !== null ? (
          <FormMessage tone="error" role="alert">
            {error}
          </FormMessage>
        ) : null}
        {formState.uploadError !== null ? (
          <FormMessage tone="error" role="alert">
            {formState.uploadError}
          </FormMessage>
        ) : null}

        {isLoading ? (
          <p className="text-sm text-text-primary/70">Loading property…</p>
        ) : (
          <Form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                id="property-code"
                label="Property code"
                description="Short identifier used in URLs and public listings. Letters/numbers plus - or _. Example: 501_ALLIANCE."
                required
                error={errorFor('propertyCode') ?? undefined}
              >
                <input
                  value={formState.propertyCode}
                  onChange={updateField('propertyCode')}
                  minLength={3}
                  maxLength={64}
                  pattern={PROPERTY_CODE_PATTERN}
                  className="w-full rounded-2xl border border-surface-subtle px-4 py-3 text-base text-text-primary shadow-sm transition focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:text-sm"
                />
              </FormField>
              <FormField
                id="submarket"
                label="Submarket"
                description="Used for public filters and portfolio reporting."
                required
                error={errorFor('submarket') ?? undefined}
              >
                <select
                  value={formState.submarket}
                  onChange={updateField('submarket')}
                  className="w-full rounded-2xl border border-surface-subtle bg-white px-4 py-3 text-base text-text-primary shadow-sm transition focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:text-sm"
                >
                  <option value="" disabled>
                    Select submarket
                  </option>
                  {SUBMARKET_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField
                id="asset-type"
                label="Asset type"
                description="Controls public labels and leasing prompts."
                required
                error={errorFor('assetType') ?? undefined}
              >
                <select
                  value={formState.assetType}
                  onChange={updateField('assetType', (value) => value as AssetType)}
                  className="w-full rounded-2xl border border-surface-subtle bg-white px-4 py-3 text-base text-text-primary shadow-sm transition focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:text-sm"
                >
                  {ASSET_TYPE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {ASSET_TYPE_LABELS[option]}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField
                id="total-rentable-sf"
                label="Total rentable SF"
                description="Total building size shown on public property pages. Example: 125000."
                required
                error={errorFor('totalRentableSf') ?? undefined}
              >
                <input
                  type="number"
                  min={1}
                  value={formState.totalRentableSf}
                  onChange={updateField('totalRentableSf')}
                  className="w-full rounded-2xl border border-surface-subtle px-4 py-3 text-base text-text-primary shadow-sm transition focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:text-sm"
                />
              </FormField>
            </div>

            <FormField
              id="marketing-summary"
              label="Marketing summary"
              required
              description="Appears in the public property hero and listing cards. Aim for 2-3 sentences."
              error={errorFor('marketingSummary') ?? undefined}
            >
              <textarea
                value={formState.marketingSummary}
                onChange={updateField('marketingSummary')}
                rows={4}
                minLength={20}
                maxLength={2000}
                className="w-full rounded-2xl border border-surface-subtle px-4 py-3 text-base text-text-primary shadow-sm transition focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:text-sm"
              />
            </FormField>

            <FormField
              id="floor-plan-mode"
              label="Floor plan mode"
              description="Controls the public floor plan experience (hotspot pins or blank canvas)."
              required
              error={errorFor('floorPlanMode') ?? undefined}
            >
              <select
                value={formState.floorPlanMode}
                onChange={updateField('floorPlanMode', (value) => value as FloorPlanMode)}
                className="w-full rounded-2xl border border-surface-subtle bg-white px-4 py-3 text-base text-text-primary shadow-sm transition focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:text-sm"
              >
                {FLOOR_PLAN_MODE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </FormField>

            <div className="bg-surface-muted/40 flex flex-col gap-3 rounded-2xl border border-surface-subtle px-4 py-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-col">
                  <span className="text-sm font-semibold text-text-primary">Floor plan image</span>
                  <span className="text-xs text-text-primary/70">
                    Shown on the public floor plan section. Upload one image,
                    {` ${maxImageSizeGb.toString()} GB`} max.
                  </span>
                </div>
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-brand-primary/40 bg-white px-3 py-2 text-sm font-semibold text-brand-primary shadow-sm transition hover:border-brand-primary hover:bg-brand-primary/10">
                  <input
                    type="file"
                    accept={PROPERTY_MEDIA_ALLOWED_TYPES.join(',')}
                    aria-label="Upload floor plan image"
                    className="sr-only"
                    onChange={handleFloorPlanFileChange}
                    disabled={formState.uploading}
                  />
                  {formState.uploading ? 'Uploading…' : 'Select image'}
                </label>
              </div>
              {formState.floorPlanImageUrl.length > 0 ? (
                <div className="relative overflow-hidden rounded-xl border border-surface-subtle bg-white">
                  <img
                    src={formState.floorPlanImageUrl}
                    alt="Uploaded floor plan preview"
                    className="h-40 w-full object-cover"
                    loading="lazy"
                  />
                  <button
                    type="button"
                    disabled={formState.uploading}
                    className="absolute right-2 top-2 rounded-full bg-black/60 px-2 py-1 text-xs font-semibold text-white shadow-sm transition hover:bg-black/80 disabled:cursor-not-allowed disabled:opacity-60"
                    onClick={() => {
                      void handleRemoveFloorPlanImage();
                    }}
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <p className="text-sm text-text-primary/60">No floor plan image uploaded yet.</p>
              )}
              {renderFieldError('floorPlanImageUrl', 'text-xs text-red-600')}
            </div>

            <div className="bg-surface-muted/40 flex flex-col gap-3 rounded-2xl border border-surface-subtle px-4 py-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-col">
                  <span className="text-sm font-semibold text-text-primary">Hero media</span>
                  <span className="text-xs text-text-primary/70">
                    Shown in the public property hero gallery. Upload up to
                    {` ${PROPERTY_MEDIA_MAX_FILES.toString()}`} photos or MP4 videos,
                    {` ${maxImageSizeGb.toString()} GB`} max each. ({mediaList.length.toString()}/
                    {PROPERTY_MEDIA_MAX_FILES.toString()})
                  </span>
                </div>
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-brand-primary/40 bg-white px-3 py-2 text-sm font-semibold text-brand-primary shadow-sm transition hover:border-brand-primary hover:bg-brand-primary/10">
                  <input
                    type="file"
                    multiple
                    accept={PROPERTY_HERO_MEDIA_ALLOWED_TYPES.join(',')}
                    aria-label="Upload hero media"
                    className="sr-only"
                    onChange={handleFileChange}
                    disabled={formState.uploading || isHeroGalleryFull}
                  />
                  {formState.uploading
                    ? 'Uploading…'
                    : isHeroGalleryFull
                      ? 'Max reached'
                      : 'Select media'}
                </label>
              </div>
              {mediaList.length > 0 ? (
                <div className="grid gap-3 sm:grid-cols-2">
                  {mediaList.map((url) => (
                    <div
                      key={url}
                      className="relative overflow-hidden rounded-xl border border-surface-subtle bg-white"
                    >
                      {isPropertyMediaVideoUrl(url) ? (
                        <video
                          src={url}
                          className="h-40 w-full object-cover"
                          controls
                          preload="metadata"
                          playsInline
                          aria-label="Uploaded property media preview"
                        />
                      ) : (
                        <img
                          src={url}
                          alt="Uploaded property media preview"
                          className="h-40 w-full object-cover"
                          loading="lazy"
                        />
                      )}
                      <button
                        type="button"
                        disabled={formState.uploading}
                        className="absolute right-2 top-2 rounded-full bg-black/60 px-2 py-1 text-xs font-semibold text-white shadow-sm transition hover:bg-black/80 disabled:cursor-not-allowed disabled:opacity-60"
                        onClick={() => {
                          void handleRemoveMedia(url);
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-text-primary/60">No media uploaded yet.</p>
              )}
              {renderFieldError('heroMediaUrls', 'text-xs text-red-600')}
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                id="street-number"
                label="Street number"
                description="Street number or range. Example: 501 or 501-503."
                required
                error={errorFor('streetNumber') ?? undefined}
              >
                <input
                  value={formState.streetNumber}
                  onChange={updateField('streetNumber')}
                  maxLength={16}
                  className="w-full rounded-2xl border border-surface-subtle px-4 py-3 text-base text-text-primary shadow-sm transition focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:text-sm"
                />
              </FormField>
              <FormField
                id="street-name"
                label="Street name"
                description="Street name without the suffix. Example: Queen."
                required
                error={errorFor('streetName') ?? undefined}
              >
                <input
                  value={formState.streetName}
                  onChange={updateField('streetName')}
                  maxLength={120}
                  className="w-full rounded-2xl border border-surface-subtle px-4 py-3 text-base text-text-primary shadow-sm transition focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:text-sm"
                />
              </FormField>
              <FormField
                id="street-type"
                label="Street type"
                description="Street suffix used in the public address (St, Ave, Rd)."
                required
                error={errorFor('streetType') ?? undefined}
              >
                <select
                  value={formState.streetType}
                  onChange={updateField('streetType', (value) => value as StreetType)}
                  className="w-full rounded-2xl border border-surface-subtle bg-white px-4 py-3 text-base text-text-primary shadow-sm transition focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:text-sm"
                >
                  {STREET_TYPE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField
                id="street-direction"
                label="Street direction"
                description="Optional directional suffix (N, NE, SW)."
                error={errorFor('streetDirection') ?? undefined}
              >
                <select
                  value={formState.streetDirection}
                  onChange={updateField('streetDirection', (value) =>
                    value === '' ? '' : (value as StreetDirection),
                  )}
                  className="w-full rounded-2xl border border-surface-subtle bg-white px-4 py-3 text-base text-text-primary shadow-sm transition focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:text-sm"
                >
                  <option value="">None</option>
                  {STREET_DIRECTION_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField
                id="suite-number"
                label="Suite number"
                description="Optional building suite if needed. Example: 100."
                error={errorFor('suiteNumber') ?? undefined}
              >
                <input
                  value={formState.suiteNumber}
                  onChange={updateField('suiteNumber')}
                  maxLength={16}
                  className="w-full rounded-2xl border border-surface-subtle px-4 py-3 text-base text-text-primary shadow-sm transition focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:text-sm"
                />
              </FormField>
              <FormField
                id="city"
                label="City"
                description="Public address city."
                required
                error={errorFor('city') ?? undefined}
              >
                <input
                  value={formState.city}
                  onChange={updateField('city')}
                  maxLength={120}
                  className="w-full rounded-2xl border border-surface-subtle px-4 py-3 text-base text-text-primary shadow-sm transition focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:text-sm"
                />
              </FormField>
              <FormField
                id="province"
                label="Province"
                description="Two-letter province code shown publicly."
                required
                error={errorFor('province') ?? undefined}
              >
                <select
                  value={formState.province}
                  onChange={updateField('province', (value) => value as ProvinceCode)}
                  className="w-full rounded-2xl border border-surface-subtle bg-white px-4 py-3 text-base text-text-primary shadow-sm transition focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:text-sm"
                >
                  {PROVINCE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField
                id="postal-code"
                label="Postal code"
                description="Canadian format A1A 1A1."
                required
                error={errorFor('postalCode') ?? undefined}
              >
                <input
                  value={formState.postalCode}
                  onChange={updateField('postalCode')}
                  maxLength={7}
                  pattern={CANADIAN_POSTAL_CODE_PATTERN}
                  className="w-full rounded-2xl border border-surface-subtle px-4 py-3 text-base uppercase text-text-primary shadow-sm transition focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:text-sm"
                />
              </FormField>
            </div>
          </Form>
        )}
      </div>
    </Drawer>
  );
}

function issuePathToField(path: (string | number)[]): FormErrorKey | null {
  const [first, second] = path;
  if (first === 'address' && typeof second === 'string' && isFormErrorKey(second)) {
    return second;
  }
  if (first === 'heroMediaUrls') {
    return 'heroMediaUrls';
  }
  if (isFormErrorKey(first)) {
    return first;
  }
  return null;
}

function mapIssues(issues: ZodIssue[]): FormErrors {
  return issues.reduce<FormErrors>((acc, issue) => {
    const field = issuePathToField(issue.path);
    if (field === null || typeof acc[field] === 'string') {
      return acc;
    }
    acc[field] = issue.message;
    return acc;
  }, {});
}

function createCorrelationId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `property-form-${Date.now().toString()}`;
}

function buildFormState(property: PropertyFormValues): FormState {
  return {
    propertyCode: property.propertyCode,
    submarket: property.submarket,
    assetType: property.assetType,
    totalRentableSf: property.totalRentableSf.toString(),
    marketingSummary: property.marketingSummary,
    floorPlanMode: property.floorPlanMode,
    floorPlanImageUrl: property.floorPlanImageUrl ?? '',
    streetNumber: property.address.streetNumber,
    streetName: property.address.streetName,
    streetType: property.address.streetType,
    streetDirection: property.address.streetDirection ?? '',
    suiteNumber: property.address.suiteNumber ?? '',
    city: property.address.city,
    province: property.address.province,
    postalCode: property.address.postalCode,
    heroMediaUrls: property.heroMediaUrls.filter((url) => url.trim().length > 0),
    uploading: false,
    uploadError: null,
  };
}

function mapSummaryToFormValues(summary: PropertySummary): PropertyFormValues {
  return {
    id: summary.id,
    propertyCode: summary.propertyCode,
    submarket: summary.submarket,
    assetType: summary.assetType,
    totalRentableSf: summary.totalRentableSf,
    marketingSummary: summary.marketingSummary,
    heroMediaUrls: summary.heroMediaUrls,
    floorPlanMode: summary.floorPlanMode,
    floorPlanImageUrl: summary.floorPlanImageUrl,
    floorPlanConfig: summary.floorPlanConfig,
    address: summary.address,
    createdAt: summary.createdAt,
    updatedAt: summary.updatedAt,
    createdBy: undefined,
    updatedBy: undefined,
  };
}
